// Recognising the one URL this SPA routes on. No React, no window (lib/
// convention) — the pathname is passed in, so App.jsx's one wiring line is the
// only place that touches `window.location`.
//
// Chatboard has no router: `server.js`'s SPA fallback serves index.html for every
// path (server.js:2121) and the app has always rendered the same dashboard
// regardless. `/copy/:id` is the first path that must render something else,
// because a copy link is a URL someone else sends you.
import { writeForecastToSearch } from "../forecast/deepLink.js";

/** The mint URL shape, verbatim: `${baseUrl()}/copy/${copyId}#${secret}` — see
 *  server/handlers/copyLinks.js:444. One path segment, optional trailing slash.
 *  `[^/?#]` rather than `.`: a pathname carries neither query nor fragment, but
 *  matching them explicitly means a caller that passes a whole URL by mistake gets
 *  no match instead of a copy id with a query string glued to it. */
const COPY_PATH_RE = /^\/copy\/([^/?#]+)\/?$/;

/**
 * A `uuid` column on both `forecast_copy_redemptions` and the owner-plane mirror,
 * so the server refuses a malformed id with 400 "copy id must be a UUID"
 * (server/handlers/copyLinks.js:684). Mirrored here — a hand-kept duplicate in the
 * same spirit as `isValidExpiryDays` in ./state.js, and for the same reason: the
 * frontend bundle cannot import the server's CJS. If UUID_RE there changes, change
 * this.
 *
 * Pre-validating buys two things beyond a saved round trip: the secret is never
 * POSTed on a request that cannot succeed, and a truncated or mistyped link gets
 * copy that says so instead of the server's schema wording.
 */
const COPY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The copy id in a `/copy/:id` pathname, or null for any other path.
 *
 * Returns the segment VERBATIM rather than a validated id: an unrecognisable id
 * still has to land on the redeem screen and be told the link looks wrong. Routing
 * it back to the dashboard instead would answer a mistyped copy link with a
 * forecast list and no explanation. Use `isCopyId` for the validity question.
 *
 * @param {unknown} pathname  `window.location.pathname`
 * @returns {string|null}
 */
export function matchCopyRoute(pathname) {
  if (typeof pathname !== "string") return null;
  const m = COPY_PATH_RE.exec(pathname);
  return m ? m[1] : null;
}

/**
 * Would the redeem endpoint accept this as a copy id?
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCopyId(value) {
  return typeof value === "string" && COPY_ID_RE.test(value);
}

/**
 * Where a redeemed copy lands: the dashboard, deep-linked to the recipient's OWN new
 * forecast. Built through the existing `forecast` query-param codec rather than a
 * hand-rolled template, so the one place that writes that param stays the one place
 * that knows its name.
 *
 * A falsy id yields the bare dashboard, which is the right answer rather than an
 * error: it is reachable from a 2xx whose body carried no usable `forecastId`, and
 * the copy exists either way — the recipient should see their forecast list.
 *
 * Lives here rather than beside the component that calls it because
 * `react-refresh/only-export-components` (an eslint ERROR in this app, not a warning)
 * refuses a non-component function export from a `.jsx` file.
 *
 * @param {string|null|undefined} forecastId
 * @returns {string} a same-origin path
 */
export function redeemedForecastPath(forecastId) {
  return `/${writeForecastToSearch("", forecastId)}`;
}
